import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';

import BlDetailsTab from './tabs/BlDetailsTab';
import LimitedAdminBlDetailsTab from './tabs/LimitedAdminBlDetailsTab';
import InvDetailsTab from './tabs/InvDetailsTab';
import BankTab from './tabs/BankTab';
import BeDetailsTab from './tabs/BeDetailsTab';
import ShipChTab from './tabs/ShipChTab';
import SummaryTab from './tabs/SummaryTab';

import FullCalculationModal from './FullCalculationModal';

import { useDataEntry } from '../../queries/dataEntryQueries';
import { useCustomers } from '../../queries/customerQueries';
import { useCreateDataEntry, useUpdateDataEntry } from '../../mutations/dataEntryMutations';

// ─── Tab definitions ───────────────────────────────────────────────────────────
const TABS = [
  { id: 'bl', label: 'BL DETAILS' },
  { id: 'inv', label: 'INV DETAILS' },
  { id: 'bank', label: 'BANK' },
  { id: 'be', label: 'BE DETAILS' },
  { id: 'ship', label: 'SHIP & CH' },
  { id: 'summary', label: 'SUMMARY' },
];

// ─── Default values — ALL fields pre-filled (types match API schema) ───────────
const DEFAULT_VALUES = {
  // BL Details — string / date / number per API
  fileName: '',
  billno: '',
  dateOfShipping: null,   // datetime
  etaDate: null,          // datetime
  pqWeightcheck: '',
  pqWeight: '',
  pqFumigation: 0,        // number
  containersNumber: '',
  commodify: '',
  importer: '',
  viewPermission: {
    selectAll: false,
    importerIds: [],
  },

  // Invoice Details
  exporter: '',
  address: '',
  invoiceno: '',
  invoicedt: null,        // datetime
  proformaInvoiceNo: '',
  proformaInvoiceDate: null, // datetime
  invoiceQuantity: 0,     // number
  invoiceRate: 0,         // number
  sblcInterestUsdRate: 0, // number
  roundOffInvAmount: 0,   // number (adds to INVOICE AMOUNT - matches API schema)
  bgAmtInterestAmt: 0,    // number
  countryOfOrigin: '',
  terms: '',
  countryFromGoods: '',

  // Bank Details
  upFrontCharges: 0,      // number
  foreignBankName: '',
  M1AccountNumber: '',
  bankDetailsM1Date: null,  // datetime
  margin10: 0,              // number
  bankDetailsM1Dom: null,   // datetime
  bankDetailsM1Mv: '',
  M2AccountNumber: '',
  bankDetailsM2Date: null,  // datetime
  margin5: 0,               // number
  bankDetailsM2Dom: null,   // datetime
  bankDetailsM2Mv: '',
  bgNumber: '',
  bgDate: null,             // datetime
  bgCharges: 0,             // number
  sblcInterestCharges: 0,   // number
  dueDate: null,            // datetime
  advice: '',
  adviceGst: '',
  exchangeRate: 0,          // number
  bankDeductionDate: null,  // datetime
  forwardContractDate: null,// datetime
  forwardcontractamount: 0, // number
  rolloverType: '',
  rolloverdays: 0,          // number

  // Rollover Same BG
  rolloverSameBgUpfrontCharges: 0,       // number
  rolloverSameBgForeignBankName: '',
  rolloverSameBgM3: '',
  rolloverSameBgM3Date: null,            // datetime
  rolloverSameBgM3Value: 0,              // number
  rolloverSameBgM3Dom: null,             // datetime
  rolloverSameBgM3Mv: '',
  rolloverSameBgBgNumber: '',
  rolloverSameBgBgDate: null,            // datetime
  rolloverSameBgBgCharges: 0,            // number
  rolloverSameBgSblcInterestCharges: 0,  // number
  rolloverSameBgDueDate: null,           // datetime
  rolloverSameBgAdvice: '',
  rolloverSameBgAdviceGst: '',
  rolloverSameBgExchangeRate: 0,         // number
  rolloverSameBgBankDeductingDate: null, // datetime
  rolloverSameBgForwardCoverDate: null,  // datetime
  rolloverSameBgForwardCoveredAmt: 0,    // number

  // Rollover New BG
  rolloverNewBgUpfrontCharges: 0,       // number
  rolloverNewBgForeignBankName: '',
  rolloverNewBgM1: '',
  rolloverNewBgM1Date: null,            // datetime
  rolloverNewBgM1Value: 0,              // number
  rolloverNewBgM1Dom: null,             // datetime
  rolloverNewBgM1Mv: '',
  rolloverNewBgM2: '',
  rolloverNewBgM2Date: null,            // datetime
  rolloverNewBgM2Value: 0,              // number
  rolloverNewBgM2Dom: null,             // datetime
  rolloverNewBgM2Mv: '',
  rolloverNewBgBgNumber: '',
  rolloverNewBgBgDate: null,            // datetime
  rolloverNewBgBgCharges: 0,            // number
  rolloverNewBgSblcInterestCharges: 0,  // number
  rolloverNewBgDueDate: null,           // datetime
  rolloverNewBgAdvice: '',
  rolloverNewBgAdviceGst: '',
  rolloverNewBgExchangeRate: 0,         // number
  rolloverNewBgBankDeductingDate: null, // datetime
  rolloverNewBgForwardCoverDate: null,  // datetime
  rolloverNewBgForwardCoveredAmt: 0,    // number

  // BE Details
  beNo: '',
  beDate: null,             // datetime
  beUsd: 0,                 // number (beUsed)
  dutyPaymentDate: null,    // datetime
  customDuty: 0,            // number
  socialWelfareCharges: 0,  // number
  beIgst: 0,                // number
  assessValue: 0,           // number

  // Shipping & CH per-container input rates (string type per API)
  shippingBill: 0,          // number
  cfsName: '',
  cfsChargesNew: '',
  chaSurveyNew: '',
  examinationChargenew: '',
  cfsGroundRentNew: '',
  freightChargeNew: '',
  unloadingNew: '',
  yardRentNew: '',
  sec49New: '',
  fumigationChargeNew: '',
  paymentOption: 'Credit',

  // Auto-calculated shipping totals (setValue'd by useEffect) — number
  cfsCharges: 0,
  chaSurvey: 0,
  examinationCharge: 0,
  cfsGroundRent: 0,
  freightCharge: 0,
  unLoading: 0,
  extraExpenses: 0,
  sec49: 0,
  fumigationCharge: 0,
  totalCft: 0,
  totalInt: 0,
  od: 0,
  agents: 30,
  yardRent: 0,
  cost: 0,

  // Summary totals (auto-calculated) — number
  totalPcs: 0,
  totalPlCbm: 0,
  totalGrossCbm: 0,
  totalNetCbm: 0,
  totalsellCbm: 0,
  totalAvgRate: 0,
  totalAmount: 0,
  roundOff: 0,
  totalInvoiceAmt: 0,
  brijesh: 0,
  paidandunpaid: false,

  // Dynamic rows
  dataEntryForm: [
    {
      containerNo: '',
      averageLength: 0,    // number
      avgCftAvgGirth: 0,   // number
      pcs: 0,              // number
      plCbm: 0,            // number
      grossCbm: 0,         // number
      netCbm: 0,           // number
      avgRate: 0,          // number
      amt: 0,              // number
      lorry: '',
      sellRate: 0,         // number
      sellCbm: 0,          // number
      brijeshComm: 0,      // number
      agent: '',
      pkl: 0,              // number
      status: 'Active',

    },
  ],
};

// Date format helpers
const toPayloadDate = (val) => {
  if (!val) return null;
  const m = moment(val);
  return m.isValid() ? m.toISOString() : null;
};

const toFormDate = (val) => {
  if (!val) return '';
  const m = moment(val);
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

// ─── API payload mapper ────────────────────────────────────────────────────────
const mapFormToPayload = (data, customersList = []) => {
  const vp = data.viewPermission || {};
  const isSelectAll = !!(vp.selectAll ?? data.selectAllImporters);
  const rawAllowed = Array.isArray(vp.importerIds)
    ? vp.importerIds
    : Array.isArray(data.allowedImporters)
      ? data.allowedImporters
      : [];
  const importerIds = isSelectAll
    ? [0]
    : rawAllowed.map((id) => (typeof id === 'number' ? id : (parseInt(id, 10) || 0)));

  // Resolve customer name only for top-level importer property
  let importerName = '';
  if (isSelectAll) {
    importerName = 'All Importers';
  } else {
    const names = rawAllowed
      .map((item) => {
        if (typeof item === 'string' && isNaN(Number(item))) {
          return item;
        }
        const found = (customersList || []).find(
          (c) => String(c?.id ?? c?.customerId) === String(item) || String(c?.customerName) === String(item)
        );
        return found?.customerName || found?.name || (typeof item === 'string' ? item : null);
      })
      .filter(Boolean);

    importerName = names.length > 0
      ? names.join(', ')
      : (data.importer && isNaN(Number(data.importer)) ? data.importer : '');
  }

  return {
    fileName: data.fileName || '',
    blNo: data.billno || '',
    dateOfShipping: toPayloadDate(data.dateOfShipping),
    etaDate: toPayloadDate(data.etaDate),
    pqWeight: data.pqWeight || '',
    pqWeightcheck: data.pqWeightcheck || '',
    pqFumigation: Number(data.pqFumigation) || 0,
    containersNumber: data.containersNumber || '',
    commodify: data.commodify || '',
    importer: importerName,
    viewPermission: {
      selectAll: isSelectAll,
      importerIds: importerIds,
    },

    exporter: data.exporter || '',
    address: data.address || '',
    invoiceNo: data.invoiceno || '',
    invoiceDt: toPayloadDate(data.invoicedt),
    proformaInvoiceNo: data.proformaInvoiceNo || '',
    proformaInvoiceDate: toPayloadDate(data.proformaInvoiceDate),
    invoiceQuantity: Number(data.invoiceQuantity) || 0,
    invoiceRate: Number(data.invoiceRate) || 0,
    sblcInterestUsdRate: Number(data.sblcInterestUsdRate) || 0,
    roundOffInvAmount: Number(data.roundOffInvAmount) || 0,
    bgAmtAndInterestAmt: Number(data.bgAmtInterestAmt) || 0,
    countryOfOrigin: data.countryOfOrigin || '',
    terms: data.terms || '',
    countryFromGoods: data.countryFromGoods || '',

    upfrontCharges: Number(data.upFrontCharges) || 0,
    foreignBankName: data.foreignBankName || '',
    m1AccountNumber: data.M1AccountNumber || '',
    bankDetailsM1Date: toPayloadDate(data.bankDetailsM1Date),
    margin10: Number(data.margin10) || 0,
    bankDetailsM1Dom: toPayloadDate(data.bankDetailsM1Dom),
    bankDetailsM1Mv: data.bankDetailsM1Mv || '',

    m2AccountNumber: data.M2AccountNumber || '',
    bankDetailsM2Date: toPayloadDate(data.bankDetailsM2Date),
    margin5: Number(data.margin5) || 0,
    bankDetailsM2Dom: toPayloadDate(data.bankDetailsM2Dom),
    bankDetailsM2Mv: data.bankDetailsM2Mv || '',

    bgNumber: data.bgNumber || '',
    bgdate: toPayloadDate(data.bgDate || data.bgdate),
    bgCharges: Number(data.bgCharges) || 0,
    sblcInterestAndCharges: Number(data.sblcInterestCharges) || 0,
    dueDate: toPayloadDate(data.dueDate),
    advice: data.advice || '',
    adviceGst: data.adviceGst || '',
    exchangeRate: Number(data.exchangeRate) || 0,
    bankDeductionDate: toPayloadDate(data.bankDeductionDate),
    forwardContractDate: toPayloadDate(data.forwardContractDate),
    contractAmount: Number(data.forwardcontractamount) || 0,

    beNo: data.beNo || '',
    beDate: toPayloadDate(data.beDate),
    beUsed: Number(data.beUsd) || 0,
    dutyPaymentDate: toPayloadDate(data.dutyPaymentDate),
    customsDuty: Number(data.customDuty) || 0,
    socialWelfareCharges: Number(data.socialWelfareCharges) || 0,
    beIgst: Number(data.beIgst) || 0,
    assessValue: Number(data.assessValue) || 0,

    shippingBill: Number(data.shippingBill) || 0,
    cfsName: data.cfsName || '',
    cfsChargesNew: data.cfsChargesNew || '',
    cfsCharges: Number(data.cfsCharges) || (Number(data.cfsChargesNew || 0) * (Number(data.containersNumber) || 0)),
    chaSurveyNew: data.chaSurveyNew || '',
    chaSurvey: Number(data.chaSurvey) || (Number(data.chaSurveyNew || 0) * (Number(data.containersNumber) || 0)),
    examinationChargenew: data.examinationChargenew || '',
    examinationCharge: Number(data.examinationCharge) || (Number(data.examinationChargenew || 0) * (Number(data.containersNumber) || 0)),
    cfsGroundRentNew: data.cfsGroundRentNew || '',
    cfsGroundRent: Number(data.cfsGroundRent) || (Number(data.cfsGroundRentNew || 0) * (Number(data.containersNumber) || 0)),
    freightChargeNew: data.freightChargeNew || '',
    unloadingNew: data.unloadingNew || '',
    yardRentNew: data.yardRentNew || '',
    sec49New: data.sec49New || '',
    sec49: Number(data.sec49) || (Number(data.sec49New || 0) * (Number(data.containersNumber) || 0)),
    fumigationChargeNew: data.fumigationChargeNew || '',
    fumigationCharges: Number(data.fumigationCharges || data.fumigationCharge) || (Number(data.fumigationChargeNew || 0) * (Number(data.containersNumber) || 0)),
    paymentOption: data.paymentOption || 'Credit',

    totalCft: Number(data.totalCft) || 0,
    totalInt: Number(data.totalInt) || 0,
    od: Number(data.od) || 0,
    agents: Number(data.agents) || 0,
    yardRent: Number(data.yardRent) || 0,
    cost: Number(data.cost) || 0,

    totalPcs: Number(data.totalPcs) || 0,
    totalGrossCbm: Number(data.totalGrossCbm) || 0,
    totalNetCbm: Number(data.totalNetCbm) || 0,
    totalAvgRate: Number(data.totalAvgRate) || 0,
    totalAmount: Number(data.totalAmount) || 0,
    roundOff: Number(data.roundOff) || 0,
    totalInvoiceAmount: Number(data.totalInvoiceAmt) || 0,
    bruesh: Number(data.brijesh) || 0,
    paidandunpaid: !!data.paidandunpaid,

    rolloverType: data.rolloverType || '',
    rolloverdays: Number(data.rolloverdays) || 0,

    rolloverSameBgUpfrontCharges: Number(data.rolloverSameBgUpfrontCharges) || 0,
    rolloverSameBgForeignBankName: data.rolloverSameBgForeignBankName || '',
    rolloverSameBgM3: data.rolloverSameBgM3 || '',
    rolloverSameBgM3Date: data.rolloverSameBgM3Date || null,
    rolloverSameBgM3Value: Number(data.rolloverSameBgM3Value) || 0,
    rolloverSameBgM3Dom: data.rolloverSameBgM3Dom || null,
    rolloverSameBgM3Mv: data.rolloverSameBgM3Mv || '',
    rolloverSameBgBgNumber: data.rolloverSameBgBgNumber || '',
    rolloverSameBgBgDate: data.rolloverSameBgBgDate || null,
    rolloverSameBgBgCharges: Number(data.rolloverSameBgBgCharges) || 0,
    rolloverSameBgSblcInterestCharges: Number(data.rolloverSameBgSblcInterestCharges) || 0,
    rolloverSameBgDueDate: data.rolloverSameBgDueDate || null,
    rolloverSameBgAdvice: data.rolloverSameBgAdvice || '',
    rolloverSameBgAdviceGst: data.rolloverSameBgAdviceGst || '',
    rolloverSameBgExchangeRate: Number(data.rolloverSameBgExchangeRate) || 0,
    rolloverSameBgBankDeductingDate: data.rolloverSameBgBankDeductingDate || null,
    rolloverSameBgForwardCoverDate: data.rolloverSameBgForwardCoverDate || null,
    rolloverSameBgForwardCoveredAmt: Number(data.rolloverSameBgForwardCoveredAmt) || 0,

    rolloverNewBgUpfrontCharges: Number(data.rolloverNewBgUpfrontCharges) || 0,
    rolloverNewBgForeignBankName: data.rolloverNewBgForeignBankName || '',
    rolloverNewBgM1: data.rolloverNewBgM1 || '',
    rolloverNewBgM1Date: data.rolloverNewBgM1Date || null,
    rolloverNewBgM1Value: Number(data.rolloverNewBgM1Value) || 0,
    rolloverNewBgM1Dom: data.rolloverNewBgM1Dom || null,
    rolloverNewBgM1Mv: data.rolloverNewBgM1Mv || '',
    rolloverNewBgM2: data.rolloverNewBgM2 || '',
    rolloverNewBgM2Date: data.rolloverNewBgM2Date || null,
    rolloverNewBgM2Value: Number(data.rolloverNewBgM2Value) || 0,
    rolloverNewBgM2Dom: data.rolloverNewBgM2Dom || null,
    rolloverNewBgM2Mv: data.rolloverNewBgM2Mv || '',
    rolloverNewBgBgNumber: data.rolloverNewBgBgNumber || '',
    rolloverNewBgBgDate: data.rolloverNewBgBgDate || null,
    rolloverNewBgBgCharges: Number(data.rolloverNewBgBgCharges) || 0,
    rolloverNewBgSblcInterestCharges: Number(data.rolloverNewBgSblcInterestCharges) || 0,
    rolloverNewBgDueDate: data.rolloverNewBgDueDate || null,
    rolloverNewBgAdvice: data.rolloverNewBgAdvice || '',
    rolloverNewBgAdviceGst: data.rolloverNewBgAdviceGst || '',
    rolloverNewBgExchangeRate: Number(data.rolloverNewBgExchangeRate) || 0,
    rolloverNewBgBankDeductingDate: data.rolloverNewBgBankDeductingDate || null,
    rolloverNewBgForwardCoverDate: data.rolloverNewBgForwardCoverDate || null,
    rolloverNewBgForwardCoveredAmt: Number(data.rolloverNewBgForwardCoveredAmt) || 0,

    userForm: (data.dataEntryForm || []).map((row) => ({
      containerNo: row.containerNo || '',
      averageLength: Number(row.averageLength) || 0,
      avgCftAvgGirth: Number(row.avgCftAvgGirth) || 0,
      pcs: Number(row.pcs) || 0,
      plCbm: Number(row.plCbm) || 0,
      grossCbm: Number(row.grossCbm) || 0,
      netCbm: Number(row.netCbm) || 0,
      avgRate: Number(row.avgRate) || 0,
      amt: (Number(row.netCbm) || 0) * (Number(row.avgRate) || 0), // always fresh
      lorry: row.lorry || '',
      sellRate: Number(row.sellRate) || 0,
      sellCbm: Number(row.sellCbm) || 0,
      brijeshComm: Number(row.brijeshComm) || 0,
      agent: row.agent || '',
      pkl: Number(row.pkl) || 0,
      status: row.status || 'Active',
    })),
  };
};

// ─── API response → form field mapper ─────────────────────────────────────────
const mapResponseToForm = (d) => {
  if (!d) return DEFAULT_VALUES;
  const rows = Array.isArray(d.userForm) && d.userForm.length > 0
    ? d.userForm
    : Array.isArray(d.dataEntryForm) && d.dataEntryForm.length > 0
      ? d.dataEntryForm
      : null;

  const viewPerm = d.viewPermission || {};
  const selectAll = !!(
    viewPerm.selectAll ??
    d.selectAllImporters ??
    d.importerSelectAll
  );
  const rawImporters = Array.isArray(viewPerm.importerIds)
    ? viewPerm.importerIds
    : Array.isArray(d.allowedImporters)
      ? d.allowedImporters
      : Array.isArray(d.importers)
        ? d.importers
        : Array.isArray(d.importerIds)
          ? d.importerIds
          : [];

  return {
    fileName: d.fileName || '',
    billno: d.blNo || d.billno || '',
    dateOfShipping: toFormDate(d.dateOfShipping),
    etaDate: toFormDate(d.etaDate),
    pqWeightcheck: d.pqWeightcheck ?? '',
    pqWeight: d.pqWeight ?? '',
    pqFumigation: d.pqFumigation || '',
    containersNumber: d.containersNumber || '',
    commodify: d.commodify || '',
    importer: d.importer || '',
    viewPermission: {
      selectAll,
      importerIds: rawImporters,
    },

    exporter: d.exporter || '',
    address: d.address || '',
    invoiceno: d.invoiceNo || d.invoiceno || '',
    invoicedt: toFormDate(d.invoiceDt || d.invoicedt),
    proformaInvoiceNo: d.proformaInvoiceNo || '',
    proformaInvoiceDate: toFormDate(d.proformaInvoiceDate || d.proformaInvoiceDt),
    invoiceQuantity: d.invoiceQuantity || '',
    invoiceRate: d.invoiceRate || '',
    sblcInterestUsdRate: d.sblcInterestUsdRate || '',
    roundOffInvAmount: d.roundOffInvAmount ?? '',
    bgAmtInterestAmt: d.bgAmtAndInterestAmt || d.bgAmtInterestAmt || '',
    countryOfOrigin: d.countryOfOrigin || '',
    terms: d.terms || '',
    countryFromGoods: d.countryFromGoods || '',

    upFrontCharges: d.upfrontCharges || d.upFrontCharges || '',
    foreignBankName: d.foreignBankName || '',
    M1AccountNumber: d.m1AccountNumber || d.M1AccountNumber || '',
    bankDetailsM1Date: toFormDate(d.bankDetailsM1Date),
    margin10: d.margin10 || '',
    bankDetailsM1Dom: toFormDate(d.bankDetailsM1Dom),
    bankDetailsM1Mv: d.bankDetailsM1Mv || '',
    M2AccountNumber: d.m2AccountNumber || d.M2AccountNumber || '',
    bankDetailsM2Date: toFormDate(d.bankDetailsM2Date),
    margin5: d.margin5 || '',
    bankDetailsM2Dom: toFormDate(d.bankDetailsM2Dom),
    bankDetailsM2Mv: d.bankDetailsM2Mv || '',
    bgNumber: d.bgNumber || '',
    bgDate: toFormDate(d.bgdate || d.bgDate),
    bgCharges: d.bgCharges || '',
    sblcInterestCharges: d.sblcInterestAndCharges || d.sblcInterestCharges || '',
    dueDate: toFormDate(d.dueDate),
    advice: d.advice || '',
    adviceGst: d.adviceGst || '',
    exchangeRate: d.exchangeRate || '',
    bankDeductionDate: toFormDate(d.bankDeductionDate),
    forwardContractDate: toFormDate(d.forwardContractDate),
    forwardcontractamount: d.contractAmount || d.forwardcontractamount || '',
    rolloverType: d.rolloverType || '',
    rolloverdays: d.rolloverdays || '',

    rolloverSameBgUpfrontCharges: d.rolloverSameBgUpfrontCharges || '',
    rolloverSameBgForeignBankName: d.rolloverSameBgForeignBankName || '',
    rolloverSameBgM3: d.rolloverSameBgM3 || '',
    rolloverSameBgM3Date: toFormDate(d.rolloverSameBgM3Date),
    rolloverSameBgM3Value: d.rolloverSameBgM3Value || '',
    rolloverSameBgM3Dom: toFormDate(d.rolloverSameBgM3Dom),
    rolloverSameBgM3Mv: d.rolloverSameBgM3Mv || '',
    rolloverSameBgBgNumber: d.rolloverSameBgBgNumber || '',
    rolloverSameBgBgDate: toFormDate(d.rolloverSameBgBgDate),
    rolloverSameBgBgCharges: d.rolloverSameBgBgCharges || '',
    rolloverSameBgSblcInterestCharges: d.rolloverSameBgSblcInterestCharges || '',
    rolloverSameBgDueDate: toFormDate(d.rolloverSameBgDueDate),
    rolloverSameBgAdvice: d.rolloverSameBgAdvice || '',
    rolloverSameBgAdviceGst: d.rolloverSameBgAdviceGst || '',
    rolloverSameBgExchangeRate: d.rolloverSameBgExchangeRate || '',
    rolloverSameBgBankDeductingDate: toFormDate(d.rolloverSameBgBankDeductingDate),
    rolloverSameBgForwardCoverDate: toFormDate(d.rolloverSameBgForwardCoverDate),
    rolloverSameBgForwardCoveredAmt: d.rolloverSameBgForwardCoveredAmt || '',

    rolloverNewBgUpfrontCharges: d.rolloverNewBgUpfrontCharges || '',
    rolloverNewBgForeignBankName: d.rolloverNewBgForeignBankName || '',
    rolloverNewBgM1: d.rolloverNewBgM1 || '',
    rolloverNewBgM1Date: toFormDate(d.rolloverNewBgM1Date),
    rolloverNewBgM1Value: d.rolloverNewBgM1Value || '',
    rolloverNewBgM1Dom: toFormDate(d.rolloverNewBgM1Dom),
    rolloverNewBgM1Mv: d.rolloverNewBgM1Mv || '',
    rolloverNewBgM2: d.rolloverNewBgM2 || '',
    rolloverNewBgM2Date: toFormDate(d.rolloverNewBgM2Date),
    rolloverNewBgM2Value: d.rolloverNewBgM2Value || '',
    rolloverNewBgM2Dom: toFormDate(d.rolloverNewBgM2Dom),
    rolloverNewBgM2Mv: d.rolloverNewBgM2Mv || '',
    rolloverNewBgBgNumber: d.rolloverNewBgBgNumber || '',
    rolloverNewBgBgDate: toFormDate(d.rolloverNewBgBgDate),
    rolloverNewBgBgCharges: d.rolloverNewBgBgCharges || '',
    rolloverNewBgSblcInterestCharges: d.rolloverNewBgSblcInterestCharges || '',
    rolloverNewBgDueDate: toFormDate(d.rolloverNewBgDueDate),
    rolloverNewBgAdvice: d.rolloverNewBgAdvice || '',
    rolloverNewBgAdviceGst: d.rolloverNewBgAdviceGst || '',
    rolloverNewBgExchangeRate: d.rolloverNewBgExchangeRate || '',
    rolloverNewBgBankDeductingDate: toFormDate(d.rolloverNewBgBankDeductingDate),
    rolloverNewBgForwardCoverDate: toFormDate(d.rolloverNewBgForwardCoverDate),
    rolloverNewBgForwardCoveredAmt: d.rolloverNewBgForwardCoveredAmt || '',

    beNo: d.beNo || '',
    beDate: toFormDate(d.beDate),
    beUsd: d.beUsed ?? d.beUsd ?? '',
    dutyPaymentDate: toFormDate(d.dutyPaymentDate),
    customDuty: d.customsDuty ?? d.customDuty ?? '',
    socialWelfareCharges: d.socialWelfareCharges || '',
    beIgst: d.beIgst || '',
    assessValue: d.assessValue || '',

    shippingBill: d.shippingBill || '',
    cfsName: d.cfsName || '',
    cfsChargesNew: d.cfsChargesNew || '',
    chaSurveyNew: d.chaSurveyNew || '',
    examinationChargenew: d.examinationChargenew || '',
    cfsGroundRentNew: d.cfsGroundRentNew || '',
    freightChargeNew: d.freightChargeNew || '',
    unloadingNew: d.unloadingNew || '',
    yardRentNew: d.yardRentNew || '',
    sec49New: d.sec49New || '',
    fumigationChargeNew: d.fumigationChargeNew || '',
    fumigationCharge: d.fumigationCharge || '',
    paymentOption: d.paymentOption || 'Credit',

    totalCft: d.totalCft || '',
    totalInt: d.totalInt || '',
    od: d.od || '',
    agents: d.agents || '',
    yardRent: d.yardRent || '',
    cost: d.cost || '',

    totalPcs: d.totalPcs || '',
    totalGrossCbm: d.totalGrossCbm || '',
    totalNetCbm: d.totalNetCbm || '',
    totalAvgRate: d.totalAvgRate || '',
    totalAmount: d.totalAmount || '',
    roundOff: d.roundOff || '',
    totalInvoiceAmt: d.totalInvoiceAmount ?? d.totalInvoiceAmt ?? '',
    brijesh: d.bruesh ?? d.brijesh ?? '',
    paidandunpaid: !!d.paidandunpaid,

    dataEntryForm: rows
      ? rows.map((row) => ({
        containerNo: row.containerNo || '',
        averageLength: row.averageLength || '',
        avgCftAvgGirth: row.avgCftAvgGirth || '',
        pcs: row.pcs || '',
        plCbm: row.plCbm || '',
        grossCbm: row.grossCbm || '',
        netCbm: row.netCbm || '',
        avgRate: row.avgRate || '',
        amt: row.amt || '',
        lorry: row.lorry || '',
        sellRate: row.sellRate || '',
        sellCbm: row.sellCbm || '',
        brijeshComm: row.brijeshComm || '',
        agent: row.agent || '',
        pkl: row.pkl || '',
        status: row.status || 'Active',
        containerImage: [],
      }))
      : DEFAULT_VALUES.dataEntryForm,
  };
};

// ─── Main Form Component ───────────────────────────────────────────────────────
const DataEntryForm = ({ onFileNameChange }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = id && id !== 'new';

  const { data: apiCustomers } = useCustomers();
  const customersList = apiCustomers?.data || apiCustomers?.items || (Array.isArray(apiCustomers) ? apiCustomers : []);

  const [activeTab, setActiveTab] = useState('bl');
  const [stagedDocuments, setStagedDocuments] = useState(null);
  const [isGlobalCalcOpen, setIsGlobalCalcOpen] = useState(false);

  // ── React Hook Form ──
  const { control, handleSubmit, setValue, setError, watch, reset, formState: { errors } } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  const { fields, append, remove } = useFieldArray({
    name: 'dataEntryForm',
    control,
  });

  // ── Watched values (all used in calculations) ──
  const {
    pqWeightcheck, pqWeight, pqFumigation, fumigationCharge,
    containersNumber, totalNetCbm,
    bgCharges, upFrontCharges, advice, adviceGst,
    margin10, margin5, customDuty, socialWelfareCharges,
    shippingBill, sblcInterestCharges, sblcInterestUsdRate,
    cfsChargesNew, chaSurveyNew, examinationChargenew, cfsGroundRentNew,
    freightChargeNew, unloadingNew, yardRentNew, sec49New,
    cfsCharges, chaSurvey, examinationCharge, cfsGroundRent,
    freightCharge, unLoading, extraExpenses, sec49,
    totalCft, totalInt, od, agents, yardRent, cost,
    dataEntryForm, roundOff, fileName, paymentOption,
  } = watch();

  // Notify parent component of file name changes
  useEffect(() => {
    if (typeof onFileNameChange === 'function') {
      onFileNameChange(fileName || '');
    }
  }, [fileName, onFileNameChange]);

  // ── Fetch existing record in edit mode ──
  const isImporter = sessionStorage.getItem('importer') === 'true';
  const userRole = sessionStorage.getItem('userRole') ?? '';
  const isLimitedAdmin = userRole.trim().toLowerCase() === 'limited admin';

  const { data: existingRecord, isSuccess: isRecordLoaded } = useDataEntry(
    isEditMode ? id : null
  );

  // ── Pre-fill form when editing (skipped for importer — show empty layout) ──
  useEffect(() => {
    if (!isImporter && isEditMode && isRecordLoaded && existingRecord) {
      reset(mapResponseToForm(existingRecord));
    }
  }, [isImporter, isEditMode, isRecordLoaded, existingRecord]);

  // ── Mutations ──
  const createMutation = useCreateDataEntry();
  const updateMutation = useUpdateDataEntry();

  // ══════════════════════════════════════════════════════════════════════════════
  // CALCULATION EFFECT — Preserved exactly from the original NewDataForm.jsx
  // ══════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    // BG-CHARGES
    const bggst = (Number(bgCharges) * 18) / 100;
    const bgInvs = Number(bgCharges) + Number(bggst);
    const bgval = Number((bgInvs * 6) / 100 / Number(totalNetCbm) / 35.315).toFixed(2);

    // UPFRONTCHARGES
    const frongst = (Number(upFrontCharges) * 18) / 100;
    const fronInvs = Number(upFrontCharges) + Number(frongst);
    const fronval = Number((fronInvs * 6) / 100 / Number(totalNetCbm) / 35.315).toFixed(2);

    // ADVICE
    const advisinst = Number(advice) + Number(adviceGst);
    const advisval = Number((advisinst * 6) / 100 / Number(totalNetCbm) / 35.315).toFixed(2);

    // MARGIN 10
    const marginval = Number((margin10 * 2.5) / 100 / Number(totalNetCbm) / 35.315).toFixed(2);

    // MARGIN 5
    const marginvalfive = Number((margin5 * 2.5) / 100 / Number(totalNetCbm) / 35.315).toFixed(2);

    // customDuty
    const customDutyval = Number((customDuty * 6) / 100 / Number(totalNetCbm) / 35.315).toFixed(2);

    // socialWelfareCharge
    const socialWelfareChargeval = Number(
      (Number(socialWelfareCharges) * 6) / 100 / Number(totalNetCbm) / 35.315
    ).toFixed(2);

    // shippingBill
    const shippingBillgst = (Number(shippingBill) * 18) / 100;
    const shippingBillInvs = Number(shippingBill) + Number(shippingBillgst);
    const shippingBillval = Number(
      (shippingBillInvs * 6) / 100 / Number(totalNetCbm) / 35.315
    ).toFixed(2);

    // PQ WEIGHT
    const pqWeightInvs = (Number(pqWeightcheck) - 1) * 200 + 3500;
    const pqWeightval = Number(
      (pqWeightInvs * 6) / 100 / Number(totalNetCbm) / 35.315
    ).toFixed(2);

    // PQ FUMIGATION
    const pqFumigationInvs = pqFumigation * 0;
    const pqFumigationVal = Number(
      (pqFumigationInvs * 6) / 100 / Number(totalNetCbm) / 35.315
    ).toFixed(2);

    // FUMIGATION CHARGES
    const fumigationChargeGst = (Number(fumigationCharge) * 18) / 100;
    const fumigationChargeInvs = Number(fumigationCharge) + Number(fumigationChargeGst);
    const fumigationChargeVal =
      isNaN(fumigationChargeInvs)
        ? 0
        : Number(
          (Number(fumigationChargeInvs) * 6) / 100 / Number(totalNetCbm) / 35.315
        ).toFixed(2);

    // CFS CHARGES
    const cfscharge = cfsChargesNew * Number(containersNumber);
    setValue('cfsCharges', cfscharge || undefined);
    const cfsGst = (Number(cfsCharges) * 18) / 100;
    const cfsChargesInvs = Number(cfsCharges) + Number(cfsGst);
    const cfsChargesVal = Number(
      (Number(cfsChargesInvs) * 6) / 100 / Number(totalNetCbm) / 35.315
    ).toFixed(2);

    // CHA SURVEY
    const chasurvey = chaSurveyNew * Number(containersNumber);
    setValue('chaSurvey', chasurvey || undefined);
    const chaGst = (Number(chaSurvey) * 18) / 100;
    const chaSurveyInvs = Number(chaSurvey) + Number(chaGst);
    const chaSurveyVal = Number(
      (Number(chaSurveyInvs) * 6) / 100 / Number(totalNetCbm) / 35.315
    ).toFixed(2);

    // EXAMINATION CHARGES
    const examinationChargenews = examinationChargenew * Number(containersNumber);
    setValue('examinationCharge', examinationChargenews || undefined);

    // CFS GROUND RENT
    const cfsGroundRentNews = cfsGroundRentNew * Number(containersNumber);
    setValue('cfsGroundRent', cfsGroundRentNews || undefined);

    // FREIGHT CHARGES
    const freightcharge = freightChargeNew * Number(containersNumber);
    setValue('freightCharge', freightcharge || undefined);
    const freightChargeGst = (Number(freightCharge) * 5) / 100;
    const freightChargeInvs = Number(freightCharge) + Number(freightChargeGst);
    const freightChargeVal = Number(
      (Number(freightChargeInvs) * 6) / 100 / Number(totalNetCbm) / 35.315
    ).toFixed(2);

    // UNLOADING
    const unloading = unloadingNew * Number(containersNumber);
    setValue('unLoading', unloading || undefined);
    const unLoadingGst = (Number(unLoading) * 18) / 100;
    const unLoadingInv = Number(unLoading) + Number(unLoadingGst);
    const unLoadingVal = Number(
      (Number(unLoadingInv) * 6) / 100 / Number(totalNetCbm) / 35.315
    ).toFixed(2);

    // EXTRA EXPENSES (Yard Rent New)
    const yardRentNews = yardRentNew * Number(containersNumber);
    setValue('extraExpenses', yardRentNews);

    // SEC 49
    const sec49News = sec49New * Number(containersNumber);
    setValue('sec49', sec49News);

    const extraExpensesGst = (Number(extraExpenses) * 18) / 100;
    const extraExpensesInvs = Number(extraExpenses) + extraExpensesGst;
    const extraExpensesVal = Number(
      (Number(extraExpensesInvs) * 6) / 100 / Number(totalNetCbm) / 35.315
    ).toFixed(2);

    // PQ weight × 4
    const pqweightss = (Number(pqWeight) * 6) / 100 / Number(totalNetCbm) / 35.315;
    const pqfumigationss = (Number(pqFumigation) * 6) / 100 / Number(totalNetCbm) / 35.315;

    // EXAMINATION CHARGES val
    const examinationGst = (Number(examinationCharge) * 18) / 100;
    const examinationChargesInvs = Number(examinationCharge) + Number(examinationGst);
    const examinationChargesVal = Number(
      (Number(examinationChargesInvs) * 6) / 100 / Number(totalNetCbm) / 35.315
    ).toFixed(2);

    // CFS GROUND RENT val
    const cfsGroundGst = (Number(cfsGroundRent) * 18) / 100;
    const cfsGroundChargesInvs = Number(cfsGroundRent) + Number(cfsGroundGst);
    const cfsGroundChargesVal = Number(
      (Number(cfsGroundChargesInvs) * 6) / 100 / Number(totalNetCbm) / 35.315
    ).toFixed(2);

    // SEC 49 val
    const sec49Gst = (Number(sec49) * 18) / 100;
    const sec49ChargesInvs = Number(sec49) + Number(sec49Gst);
    const sec49ChargesVal = Number(
      (Number(sec49ChargesInvs) * 6) / 100 / Number(totalNetCbm) / 35.315
    ).toFixed(2);

    // TOTAL INT
    const totalintval = [
      bgval, fronval, advisval, marginval, marginvalfive,
      customDutyval, socialWelfareChargeval, shippingBillval,
      pqWeightval, pqFumigationVal, fumigationChargeVal,
      cfsChargesVal, chaSurveyVal, freightChargeVal, unLoadingVal,
      extraExpensesVal, pqfumigationss, pqweightss,
      examinationChargesVal, cfsGroundChargesVal, sec49ChargesVal,
    ].reduce((sum, val) => sum + (Number(val) || 0), 0).toFixed(2);

    const totalintvals = Number(totalintval);
    if (!isNaN(totalintvals) && Number.isFinite(totalintvals)) {
      setValue('totalInt', totalintvals);
    } else {
      setValue('totalInt', 0);
    }

    // SBLC INTEREST AND CHARGES, BG CHARGES, UPFRONT CHARGES
    // Included in Per CFT Cost when paymentOption is 'Credit', excluded when 'Cash'
    const isCredit = String(paymentOption || 'Credit').toLowerCase() === 'credit';

    const sblcInterestChargesVal = isCredit && Number(totalNetCbm) > 0
      ? Number((Number(sblcInterestCharges) * Number(sblcInterestUsdRate)) / Number(totalNetCbm) / 35.315).toFixed(2)
      : 0;

    const bgChargesCft = isCredit && Number(totalNetCbm) > 0
      ? Number(Number(bgCharges) / Number(totalNetCbm) / 35.315).toFixed(2)
      : 0;

    const upFrontChargesCft = isCredit && Number(totalNetCbm) > 0
      ? Number(Number(upFrontCharges) / Number(totalNetCbm) / 35.315).toFixed(2)
      : 0;
    const adviceCft = Number(advice) / Number(totalNetCbm) / 35.315;
    const customDutyCft = Number(Number(customDuty) / Number(totalNetCbm) / 35.315).toFixed(2);
    const socialWelfareChargeCft = Number(Number(socialWelfareCharges) / Number(totalNetCbm) / 35.315).toFixed(2);
    const shippingBillCft = Number(Number(shippingBill) / Number(totalNetCbm) / 35.315).toFixed(2);
    const pqWeightCft = Number(Number(pqWeightInvs) / Number(totalNetCbm) / 35.315).toFixed(2);
    const pqFumigationCft = Number(Number(pqFumigation) / Number(totalNetCbm) / 35.315).toFixed(2);
    const fumigationChargesCft =
      isNaN(fumigationChargeInvs)
        ? 0
        : Number(Number(fumigationChargeInvs) / Number(totalNetCbm) / 35.315).toFixed(2);
    const cfsChargesCft = Number(Number(cfsCharges) / Number(totalNetCbm) / 35.315).toFixed(2);
    const chaSurveyCft = Number(Number(chaSurvey) / Number(totalNetCbm) / 35.315).toFixed(2);
    const freightChargeCft = Number(Number(freightCharge) / Number(totalNetCbm) / 35.315).toFixed(2);
    const unLoadingCft = Number(Number(unLoading) / Number(totalNetCbm) / 35.315).toFixed(2);
    const extraExpensesCft = Number(Number(extraExpenses) / Number(totalNetCbm) / 35.315).toFixed(2);
    const examinationChargeCft = Number(Number(examinationCharge) / Number(totalNetCbm) / 35.315).toFixed(2);
    const cfsGroundChargeCft = Number(Number(cfsGroundRent) / Number(totalNetCbm) / 35.315).toFixed(2);
    const sec49ChargeCft = Number(Number(sec49) / Number(totalNetCbm) / 35.315).toFixed(2);
    const pqweight4timesCft = Number(pqWeight) / Number(totalNetCbm) / 35.315;

    const totalCftVal = [
      sblcInterestChargesVal, bgChargesCft, upFrontChargesCft, adviceCft,
      customDutyCft, socialWelfareChargeCft, shippingBillCft, pqWeightCft,
      pqFumigationCft, fumigationChargesCft, cfsChargesCft, chaSurveyCft,
      freightChargeCft, unLoadingCft, extraExpensesCft, examinationChargeCft,
      sec49ChargeCft, cfsGroundChargeCft, pqweight4timesCft,
    ].reduce((sum, val) => sum + (Number(val) || 0), 0).toFixed(2);

    const a = Number(totalCftVal);
    if (!isNaN(a) && Number.isFinite(a)) {
      setValue('totalCft', a);
    } else {
      setValue('totalCft', 0);
    }

    // OD
    setValue('od', totalInt || undefined);
    // YARD RENT
    const yardRentVal = (Number(containersNumber) * 3000) / Number(totalNetCbm) / 35.315;
    setValue('yardRent', yardRentVal.toFixed(2) || undefined);
    // COST (Per Cft + total Interest + agents)
    const costVal = Number((Number(totalCft || 0) + Number(totalInt || 0) + Number(agents || 0)).toFixed(2));
    setValue('cost', costVal || 0);
  }, [
    cfsChargesNew, chaSurveyNew, unloadingNew, sec49New,
    sblcInterestCharges, yardRentNew, sblcInterestUsdRate,
    totalInt, extraExpenses, unLoading, freightCharge, chaSurvey,
    containersNumber, cfsCharges, fumigationCharge, pqFumigation,
    pqWeight, pqWeightcheck, shippingBill, socialWelfareCharges,
    customDuty, bgCharges, margin5, margin10, upFrontCharges,
    adviceGst, advice, totalCft, cost, od, yardRent, agents,
    examinationChargenew, cfsGroundRentNew, freightChargeNew,
    sec49, cfsGroundRent, examinationCharge, totalNetCbm, paymentOption,
  ]);

  // NOTE: Summary totals (totalNetCbm, totalAmount, etc.) are now computed and
  // synced by SummaryTab via useWatch({ control }) — no duplication here.

  // ── Form submit ──
  const onSubmit = async (data) => {
    const payload = mapFormToPayload(data, customersList);

    if (isEditMode) {
      updateMutation.mutate(
        { id, data: payload },
        {
          onSuccess: () => navigate('/business/data-management'),
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => navigate('/business/data-management'),
      });
    }
  };

  const visibleTabs = TABS.filter((tab) => !isLimitedAdmin || tab.id === 'bl' || tab.id === 'summary');
  const tabIndex = visibleTabs.findIndex((t) => t.id === activeTab);

  const goNext = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (tabIndex < visibleTabs.length - 1) setActiveTab(visibleTabs[tabIndex + 1].id);
  };
  const goPrev = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (tabIndex > 0) setActiveTab(visibleTabs[tabIndex - 1].id);
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
          e.preventDefault();
        }
      }}
      className="card shadow-sm border rounded-top-0 rounded-bottom-3 d-flex flex-column flex-grow-1 overflow-hidden"
      noValidate
    >

      {/* ── Tab Navigation + Back Button ── */}
      <div className="de-tab-nav-card">
        <div className="de-tab-nav-tabs">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`de-tab-btn${activeTab === tab.id ? ' active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab(tab.id);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="d-flex align-items-center gap-2">
          {!isLimitedAdmin && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary fw-bold text-uppercase px-3"
              onClick={() => setIsGlobalCalcOpen(true)}
              style={{ fontSize: '11.5px', height: '31px' }}
            >
              🔍 Calc Preview
            </button>
          )}
          <button
            type="button"
            className="de-back-icon-btn"
            onClick={() => navigate('/business/data-management')}
            title="Back to List"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Tab Content (scrollable) ── */}
      <div className="flex-grow-1 overflow-auto de-form-body">
        {activeTab === 'bl' && (
          isLimitedAdmin ? (
            <LimitedAdminBlDetailsTab control={control} />
          ) : (
            <BlDetailsTab control={control} errors={errors} setValue={setValue} />
          )
        )}
        {activeTab === 'inv' && <InvDetailsTab control={control} setValue={setValue} errors={errors} />}
        {activeTab === 'bank' && <BankTab control={control} watch={watch} errors={errors} />}
        {activeTab === 'be' && <BeDetailsTab control={control} errors={errors} />}
        {activeTab === 'ship' && <ShipChTab control={control} watch={watch} errors={errors} />}
        {activeTab === 'summary' && (
          <SummaryTab
            control={control}
            watch={watch}
            fields={fields}
            append={append}
            remove={remove}
            setValue={setValue}
            dataEntryId={isEditMode ? id : null}
          />
        )}

      </div>

      {/* ── Global Full Calculation Sheet Modal ── */}
      <FullCalculationModal
        isOpen={isGlobalCalcOpen}
        onClose={() => setIsGlobalCalcOpen(false)}
        liveValues={watch()}
      />

      {/* ── Footer: Prev / Next / Save ── */}
      <div className="de-form-footer">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={(e) => {
            e.preventDefault();
            goPrev(e);
          }}
          disabled={tabIndex <= 0}
        >
          ← Prev
        </button>

        {activeTab === 'summary' ? (
          <button
            type="submit"
            className="btn btn-orange"
            disabled={isPending}
            style={{ minWidth: '220px', padding: '11px 48px', fontSize: '16px', fontWeight: 700 }}
          >
            {isPending ? 'Saving...' : isEditMode ? '💾 Update' : '✅ Submit'}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-orange"
            onClick={(e) => {
              e.preventDefault();
              goNext(e);
            }}
          >
            Next →
          </button>
        )}
      </div>
    </form>
  );
};

export default DataEntryForm;
